import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, Instagram, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message envoyé !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#FAFAFA]" data-testid="contact-success">
        <div className="max-w-md mx-auto text-center p-12 bg-white border border-[#E5E5E5]">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-['Playfair_Display'] mb-4">Message envoyé !</h1>
          <p className="text-[#666666] mb-8 font-['Outfit']">
            Nous vous répondrons dans les plus brefs délais.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            className="border-[#0A0A0A]"
            data-testid="new-message-btn"
          >
            Envoyer un autre message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#FAFAFA]" data-testid="contact-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white py-16">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] mb-4">Contact</h1>
          <p className="text-white/70 font-['Outfit']">Une question concernant votre séjour ? Nous sommes à votre écoute.</p>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Layout vertical : Comment nous joindre -> Photo -> Formulaire */}
        <div className="max-w-3xl mx-auto space-y-12">
          
          {/* Contact Info */}
          <div className="space-y-8" data-testid="contact-info">
            <div className="text-center">
              <span className="text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4 block font-['Outfit']">
                Domaine du Normont
              </span>
              <h2 className="text-3xl font-['Playfair_Display'] text-[#0A0A0A] mb-6">
                Comment nous joindre ?
              </h2>
              <p className="text-[#666666] font-['Outfit'] leading-relaxed max-w-xl mx-auto">
                Pour toute question concernant votre séjour, une réservation ou un événement, 
                n'hésitez pas à nous contacter. Notre équipe vous répondra dans les plus brefs délais.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 bg-white p-4 border border-[#E5E5E5]">
                <div className="w-12 h-12 bg-[#FAFAFA] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] font-['Outfit']">Email</h3>
                  <p className="text-[#666666] font-['Outfit']">contact@domainedunormont.fr</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white p-4 border border-[#E5E5E5]">
                <div className="w-12 h-12 bg-[#FAFAFA] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] font-['Outfit']">Téléphone</h3>
                  <p className="text-[#666666] font-['Outfit']">Sur demande</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white p-4 border border-[#E5E5E5]">
                <div className="w-12 h-12 bg-[#FAFAFA] flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] font-['Outfit']">Localisation</h3>
                  <p className="text-[#666666] font-['Outfit']">45 minutes de Paris</p>
                  <p className="text-sm text-[#666666] font-['Outfit']">Adresse communiquée après réservation</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white p-4 border border-[#E5E5E5]">
                <div className="w-12 h-12 bg-[#FAFAFA] flex items-center justify-center shrink-0">
                  <Instagram className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0A0A0A] font-['Outfit']">Instagram</h3>
                  <a 
                    href="https://instagram.com/domainedunormont" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#C5A059] hover:underline font-['Outfit']"
                  >
                    @domainedunormont
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Image - between the two sections */}
          <div className="img-zoom">
            <img 
              src="https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/42vd3e3r_soiree.png"
              alt="Domaine du Normont"
              className="w-full h-auto object-contain rounded"
            />
          </div>

          {/* Contact Form */}
          <div className="bg-white border border-[#E5E5E5] p-8 md:p-12" data-testid="contact-form-section">
            <h3 className="text-2xl font-['Playfair_Display'] mb-6 text-center">Envoyez-nous un message</h3>
            
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Nom complet *</Label>
                    <Input 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Jean Dupont"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label className="font-['Outfit'] mb-2 block">Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="jean@exemple.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-['Outfit'] mb-2 block">Téléphone</Label>
                  <Input 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <Label className="font-['Outfit'] mb-2 block">Message *</Label>
                  <textarea 
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full border border-[#E5E5E5] p-4 h-32 resize-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
                    placeholder="Votre message..."
                    required
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white py-6"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
